import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  SimpleChanges,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Data } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { map, noop, switchMap, tap } from 'rxjs';

import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import cloneDeep from 'lodash-es/cloneDeep';
import { CustomObject, CustomObjectValue } from '../custom-objects.types';
import { FieldDef, SavedView } from 'app/core/models';
import { Dictionary } from 'app/shared/models';
import { CustomObjectActions } from '../state/actions';
import { CustomField, CustomFormField, CustomFormFieldType } from '../../custom-form/custom-form.types';
import { isArraysEqual, keepProps } from 'app/shared/Utils/common.utils';
import { ProjectTemplate } from 'app/modules/projects/projects.types';
import { AccountService } from 'app/core';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { RelatedToType } from 'app/shared/enums';
import { hasLinkedField } from '../custom-objects.functions';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';
import { PermissionUtils } from '@core/utils/permission.utils';

@Component({
  selector: 'custom-objects-quick-view',
  templateUrl: './quick-view.component.html',
  styleUrls: ['./quick-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomObjectsQuickViewComponent extends OnDestroyMixin implements OnInit, OnChanges {
  public data: Data;
  public allowedFields: FieldDef[];
  public selectedFieldDef: FieldDef;
  public selectedFields: CustomField[];
  public currentFields: CustomFormField[];
  public templates: ProjectTemplate<any>[];
  public template_id = new FormControl();
  public ui = { isEditing: false, isLoading: false, isAddingField: false, isSaving: false };

  @Input() customObjectValue: CustomObjectValue;
  @Input() customObject: CustomObject;
  @Input() customObjectId: number;
  @Input() operation: 'create' | 'update';
  @Input() relatedToType: string;
  @Input() relatedToId: number;
  @Input() savedView: SavedView;
  @Input() set templateId(value: number) {
    if (value) {
      this.template_id.setValue(value);
    }
  }

  @Output() operationCompleted = new EventEmitter<boolean>();

  private get isTemplateDirty(): boolean {
    return !isArraysEqual(this.normalizeArray(this.selectedFields), this.normalizeArray(this.currentFields));
  }

  private get isFormInvalid(): boolean {
    return this.customObjectValue && Object.keys(this.customObjectValue).length > 0 && this.template_id.value;
  }

  get isSaveButtonDisabled(): boolean {
    return !this.isFormInvalid || this.ui.isSaving;
  }

  get canSaveTemplate(): boolean {
    return this.isTemplateDirty && !this.ui.isAddingField;
  }

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly accountService: AccountService,
    private readonly projectsService: ProjectsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.data = this.activatedRoute.snapshot.data;

    this.activatedRoute.data
      .pipe(
        untilComponentDestroyed(this),
        switchMap((data: Data) => {
          this.projectsService.bustProjectTemplatesCache();

          return this.projectsService.getProjectTemplates().pipe(
            untilComponentDestroyed(this),
            map((result: ProjectTemplate<any>[]) => {
              this.templates = result
                .filter(({ related_to_type }) => related_to_type === this.customObject.name)
                .sort((a, b) => {
                  if (a.rank < b.rank) return -1;
                  if (a.rank > b.rank) return 1;
                  return 0;
                });

              this.template_id.setValue(this.templates[0].id);

              this.data = {
                customObjectId: this.customObjectId,
                ...data,
              };
            }),
          );
        }),
      )
      .subscribe(noop);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.customObject) this.setupInitialFields();

    if (changes.operation) {
      if (this.operation === 'create' && !Object.keys(this.customObjectValue).length) {
        this.customObjectValue = {};
      }
      this.setCurrentFields();
    }
  }

  private setupInitialFields(): void {
    this.allowedFields = this.customObject.custom_fields.filter((field) => !field.archived);
    this.selectedFields = this.allowedFields.map((field) => ({ name: field.name, isRequired: false }));
  }

  private refreshCurrentFields(): void {
    const formDefinition = JSON.parse(this.savedView.definition)?.custom_object_form_definition;
    this.currentFields =
      formDefinition && JSON.parse(formDefinition).fields.length > 0 && this.operation === 'create'
        ? JSON.parse(formDefinition).fields.map((selectedField: CustomField) => this.toCustomFormField(selectedField))
        : this.selectedFields?.map((selectedField: CustomField) => this.toCustomFormField(selectedField));
  }

  private setCurrentFields(): void {
    this.ui = { ...this.ui, isLoading: true };
    this.refreshCurrentFields();
    this.ui = { ...this.ui, isLoading: false };
  }

  private toCustomFormField(customField: CustomField): CustomFormField {
    const { name: fieldName } = customField;
    return {
      name: fieldName,
      type: CustomFormFieldType.FieldDef,
      fieldDef: this.allowedFields.find(({ name }) => name === fieldName),
      ...customField,
    };
  }

  private normalizeArray(arr: CustomField[]): CustomField[] {
    return arr.map((item) => keepProps(item, ['name', 'isRequired']) as CustomField);
  }

  private setupActions(): void {
    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(CustomObjectActions.CreateValue, CustomObjectActions.UpdateValue),
        tap(() => {
          this.close();
          this.ui = { ...this.ui, isLoading: false, isSaving: false };
        }),
      )
      .subscribe(noop);
  }

  public toggleEditing(): void {
    if (this.ui.isEditing && this.isTemplateDirty) this.refreshCurrentFields();
    this.ui = { ...this.ui, isEditing: !this.ui.isEditing, isAddingField: false };
  }

  public dropped(event: CdkDragDrop<FieldDef[]>): void {
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
  }

  public isSelected(fieldName: string): boolean {
    return this.currentFields.filter(({ name }) => name === fieldName).length > 0;
  }

  public onFieldSelected(event: MatSelectChange): void {
    this.selectedFieldDef = event.value as FieldDef;
  }

  public addField(): void {
    this.ui = { ...this.ui, isAddingField: true };
    this.currentFields = [
      ...this.currentFields,
      { name: CustomFormFieldType.Placeholder, type: CustomFormFieldType.Placeholder },
    ];
  }

  public removeField(fieldIndex: number): void {
    if (this.currentFields[fieldIndex].type === CustomFormFieldType.Placeholder) {
      this.ui = { ...this.ui, isAddingField: false };
    }

    this.currentFields.splice(fieldIndex, 1);
  }

  public saveField(fieldIndex: number): void {
    this.currentFields[fieldIndex] = {
      name: this.selectedFieldDef.name,
      type: CustomFormFieldType.FieldDef,
      fieldDef: this.selectedFieldDef,
    };
    this.ui = { ...this.ui, isAddingField: false };
    this.selectedFieldDef = null;
  }

  public saveTemplate(): void {
    const fields = this.normalizeArray(this.currentFields);
    if (this.operation === 'create') this.updateCustomObjectFormDefinition(fields);
    this.selectedFields = fields;
    this.ui = { ...this.ui, isEditing: !this.ui.isEditing };
  }

  private updateCustomObjectFormDefinition(fields: CustomField[]): void {
    this.savedView.definition = JSON.stringify({
      ...JSON.parse(this.savedView.definition || '{}'),
      custom_object_form_definition: JSON.stringify({
        fields: fields,
      }),
    });

    this.accountService
      .updateSavedView(this.savedView)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  /**
   * Check if the field is a relational field (created by BE from adding relationship) and if it matches the current related type
   * @param drawerFields
   * @returns True if the field is a relational field and matches the current related type, false otherwise
   */
  isReadOnly(drawerFields: { name: string; type: string; isRequired: boolean; fieldDef: FieldDef }): boolean {
    const { fieldDef } = drawerFields;

    if (!hasLinkedField(fieldDef)) {
      return false;
    }

    if (fieldDef.meta?.record_type === RelatedToType.Company && this.relatedToType === RelatedToType.Company) {
      return true;
    }
    if (fieldDef.meta?.record_type === RelatedToType.Contact && this.relatedToType === RelatedToType.Contact) {
      return true;
    }
    if (fieldDef.meta?.record_type === RelatedToType.Project && this.relatedToType === RelatedToType.Project) {
      return true;
    }
    if (fieldDef.meta?.record_type === RelatedToType.Property && this.relatedToType === RelatedToType.Property) {
      return true;
    }
    if (
      fieldDef.meta?.record_type === this.customObjectValue?.customObject?.name &&
      this.relatedToType === this.customObjectValue?.customObject?.name
    ) {
      return true;
    }

    return false;
  }

  public onFieldUpdate(event: Dictionary<any>): void {
    this.customObjectValue = { ...this.customObjectValue, ...event };
  }

  save(): void {
    this.ui = { ...this.ui, isLoading: true, isSaving: true };

    /**
     * [SC-48695] WIP:
     * For both operations, BE service is not ready to handle value updates at customObjectValue top-level attributes.
     * That's why we need to pass the customObjectValue as is inside the data attribute,
     * and the BE will handle the modifications at the customObjectValue level.
     */
    const customObjectValue: CustomObjectValue = {
      // [SC-48695] Commented out because BE is not ready to handle value updates at customObjectValue top-level attributes.
      // ...this.customObjectValue,
      data: this.customObjectValue,
      related_to_id: this.relatedToId,
      related_to_type: this.relatedToType,
      template_id: this.template_id.value,
    };

    this.setupActions();

    if (this.operation === 'create') {
      this.store.dispatch(new CustomObjectActions.CreateValue(this.customObjectId, customObjectValue));
    } else {
      this.store.dispatch(
        new CustomObjectActions.UpdateValue(this.customObjectId, this.customObjectValue.id, customObjectValue),
      );
    }
  }

  close(): void {
    this.operationCompleted.emit(false);
    this.ui = { isLoading: false, isSaving: false, isAddingField: false, isEditing: false };
  }

  getManageAddFormCustomObjectPermission(customObjectName: string): string | boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return PermissionUtils.generateCustomObjectPermissions(customObjectName).MANAGE_ADD_FORM;
    } else {
      return true;
    }
  }
}
